import { Component } from '@angular/core';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, KeyValue } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { Pair } from '../../interfaces/pair';
import { ReasonInactive } from '../../interfaces/pair';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PairService } from '../../services/pair.service';
import { ParticipantBoxComponent } from '../participant/participant-box/participant-box.component';
import { MatDialog } from '@angular/material/dialog';
import { PairAddComponent } from './pair-add/pair-add.component';
import { MatchingPeriodService } from '../../services/matchingPeriod.service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { PairEditComponent } from './pair-edit/pair-edit.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { Permission } from '../../interfaces/permission';
import { AuthService } from '../../auth/auth.service';
import { RemoveDialogComponent } from './remove-dialog/remove-dialog.component';
import { WarningChipsComponent } from '../../shell/warning-chips/warning-chips.component';

@Component({
  selector: 'app-pair',
  imports: [
    PagetoolbarComponent,
    MatIconModule,
    CommonModule,
    MatExpansionModule,
    MatNativeDateModule,
    MatDatepickerModule,
    ParticipantBoxComponent,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatInputModule,
    HighlightPipe,
    MatButtonModule,
    MatMenuModule,
    RemoveDialogComponent,
    WarningChipsComponent
  ],
  templateUrl: './pairs.component.html',
  styleUrl: './pairs.component.scss'
})
export class PairsComponent {
  pairs: Pair[] = [];
  filteredPairs: Pair[] = [];
  // Filter controls
  searchControl = new FormControl('');
  activeFilterMode$ = new BehaviorSubject<string>('all'); // 'all', 'active', 'inactive'
  activeLabel$ = this.activeFilterMode$.pipe(map((m) => (m === 'active' ? 'Show Inactive' : 'Show Active')));
  // Permissions
  canAddPair: boolean = false;
  canEditPair: boolean = false;
  canRemovePair: boolean = false;
  canInactivatePair: boolean = false;
  isCenterUser: boolean = false;
  hasMatchrunStarted: boolean = false;

  panelOpenState: boolean[] = [];

  constructor(
    private snackBar: MatSnackBar,
    private pairService: PairService,
    private matchingPeriodService: MatchingPeriodService,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService
  ) {
    this.activeFilterMode$.next('all'); // Set default filter mode
  }

  ngOnInit(): void {
    // Combine all observables to ensure proper timing
    combineLatest([
      this.authService.getAllPermissions$(),
      this.authService.getUser(),
      this.matchingPeriodService.getLatestMatchingPeriod()
    ]).subscribe(([permissions, user, period]) => {
      // Set base permissions
      this.canAddPair = permissions.includes(Permission.PAIRS_LINK_PARTICIPANTS);
      this.canEditPair = permissions.includes(Permission.PAIRS_EDIT);
      this.canRemovePair = permissions.includes(Permission.PAIRS_EDIT);
      this.canInactivatePair = permissions.includes(Permission.PAIRS_INACTIVATE);

      // Set role-based flags
      const role = user.activeRole.enumName;
      this.isCenterUser = role === 'CENTER';

      // Check if at least one matchrun has started
      this.hasMatchrunStarted = !!(period?.matchRuns && period.matchRuns.length > 0);

      // Apply CENTER user restrictions after first matchrun starts
      if (this.isCenterUser && this.hasMatchrunStarted) {
        this.canEditPair = false;
        this.canInactivatePair = false;
      }
    });

    this.loadPairs();

    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
      this.applySearch(value);
    });
  }

  private loadPairs(): void {
    this.pairService.getAllPairs().subscribe({
      next: (data) => {
        this.pairs = data;
        this.filteredPairs = data;
        this.panelOpenState = new Array(this.filteredPairs.length).fill(false);
        this.applyFilters();
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  addPair() {
    const dialogRef = this.dialog.open(PairAddComponent);

    // Subscribe to the response without closing the dialog
    dialogRef.componentInstance.responseSubjectNewPair.subscribe((response: Pair) => {
      console.log('Received response:', response);
      this.pairs.unshift(response);
      this.applyFilters();
      // Close all expansion panels
      this.panelOpenState = new Array(this.filteredPairs.length).fill(false);
    });
  }

  removePair(pair: Pair) {
    const dialogRef = this.dialog.open(RemoveDialogComponent, {
      data: pair
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.pairs = this.pairs.filter((item) => item.id !== pair.id);
        this.applyFilters();
        // Close all expansion panels
        this.panelOpenState = new Array(this.filteredPairs.length).fill(false);
      }
    });
  }

  editPair(pair: Pair) {
    const dialogRef = this.dialog.open(PairEditComponent, {
      data: { currentPair: pair } // Pass the item data here
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Find the index of the pair with the same id
        const index = this.pairs.findIndex((p) => p.id === result.id);

        // Replace the existing pair if found
        if (index !== -1) {
          this.pairs[index] = result;
        } else {
          console.warn('Pair with the specified ID was not found.');
        }

        this.applyFilters();
        console.log('Updated pair:', result);
      }
    });
  }

  onPairUpdated(updatedPair: Pair): void {
    console.log('Pair updated from warning chips:', updatedPair);
    // Find the index of the pair with the same id
    const index = this.pairs.findIndex((p) => p.id === updatedPair.id);

    // Replace the existing pair if found
    if (index !== -1) {
      this.pairs[index] = updatedPair;
    } else {
      console.warn('Pair with the specified ID was not found.');
    }

    this.applyFilters();
  }

  //The difference between activate/deactive pair is that the deactive gives the Pair object to the backend. This is done to add a reason to the inactive version
  activatePair(pair: Pair) {
    this.matchingPeriodService.activatePair(pair.id).subscribe({
      next: (updatedPair) => {
        const index = this.pairs.findIndex((p) => p.id === pair.id);
        if (index !== -1) {
          this.pairs[index] = updatedPair; // Update the pair in the array
        }

        this.applyFilters();
      },
      error: (err) => console.error(err)
    });
  }

  deactivatePair(pair: Pair) {
    this.matchingPeriodService.deactivatePair(pair).subscribe({
      next: (updatedPair) => {
        const index = this.pairs.findIndex((p) => p.id === pair.id);
        if (index !== -1) {
          this.pairs[index] = updatedPair; // Update the pair in the array
        }

        this.applyFilters();
      },
      error: (err) => console.error(err)
    });
  }
  applyFilters(): void {
    this.filteredPairs = this.pairs.filter((pair) => {
      const matchesActive = this.activeFilterMode$.value === 'all' || (this.activeFilterMode$.value === 'active' ? pair.active : !pair.active);
      return matchesActive;
    });
  }
  applySearch(searchTerm: string | null): void {
    if (searchTerm == null) {
      this.loadPairs();
      return;
    }

    searchTerm = searchTerm.toLowerCase();
    this.filteredPairs = this.pairs.filter((pair) => {
      return (
        pair.donor.enisNumber.toLowerCase().includes(searchTerm) ||
        pair.donor.firstName?.toLowerCase().includes(searchTerm) ||
        pair.donor.lastName?.toLowerCase().includes(searchTerm) ||
        pair.recipient.enisNumber.toLowerCase().includes(searchTerm) ||
        pair.recipient.firstName?.toLowerCase().includes(searchTerm) ||
        pair.recipient.lastName?.toLowerCase().includes(searchTerm)
      );
    });
  }

  toggleActiveFilter(): void {
    const current = this.activeFilterMode$.value;
    this.activeFilterMode$.next(current === 'active' ? 'inactive' : 'active');
    this.applyFilters();
  }


  showAll(): void {
    this.activeFilterMode$.next('all');
    this.applyFilters();
  }

  private showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  withdrawPair(pair: Pair, reason: KeyValue<string, ReasonInactive>): void {
    //Set reason and deactivate
    pair.reasonInactive = reason.value;
    pair.active = false;
    this.matchingPeriodService.deactivatePair(pair).subscribe({
      next: (updatedPair) => {
        const index = this.pairs.findIndex((p) => p.id === pair.id);
        if (index !== -1) {
          this.pairs[index] = updatedPair; // Update the pair in the array
        }

        this.applyFilters();
        console.log('Pair deactivated:', updatedPair);
        this.showNotification('Pair set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error editing pair:', err);
        this.showNotification('Failed to set pair inactive. Please try again.');
      }
    });
  }

  getWithdrawReasons(): Record<string, ReasonInactive> {
    // Exclude automatic reasons that should only be set by the system
    return {
      MedicalProblemsPatient: ReasonInactive.MedicalProblemsPatient,
      MedicalProblemsDonor: ReasonInactive.MedicalProblemsDonor,
      PhychologicalProblemsPatient: ReasonInactive.PhychologicalProblemsPatient,
      PhychologicalProblemsDonor: ReasonInactive.PhychologicalProblemsDonor,
      Other: ReasonInactive.Other
    };
  }

  // Helper method to check if a pair can be activated
  canActivatePairEntity(pair: Pair | null | undefined): boolean {
    if (!pair) return false;
    return pair.reasonInactive !== ReasonInactive.AwaitingNewMatchRunPeriod;
  }
}
